import { useCallback, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { ExistingSupplierMatch } from '../../../lib/api';
import type { SupplierFormData } from './types';

interface UseExistingSupplierSelectionReturn {
  /** The picked supplier's Company id, or null when creating a brand-new supplier. */
  existingSupplierId: number | null;
  /** The picked supplier's record — used to lock only the fields it already fills. */
  selectedMatch: ExistingSupplierMatch | null;
  /** Pick an existing supplier from the Step 2 typeahead. */
  selectExisting: (match: ExistingSupplierMatch) => void;
  /** Free-text edit of the supplier name; abandons any current selection. */
  changeSupplierName: (name: string) => void;
  /** Drop the selection and clear the fields that were pre-filled from it. */
  clearExisting: () => void;
  /** True when an existing supplier already has a value for `key` (so lock it). */
  lockedField: (key: keyof ExistingSupplierMatch) => boolean;
}

/**
 * CR-6685 (onboarding): manage picking an existing supplier from the Step 2
 * name typeahead so the wizard links to it instead of creating a duplicate.
 *
 * Picking a match pre-fills the supplier-identity fields the wizard collects
 * (name + structured address + currency) and remembers the supplier id so
 * `ensureSupplierCreated` links rather than creates. Typing a fresh name
 * abandons the selection and clears only those pre-filled fields — the
 * customer / routing / contact data the buyer configured is left intact.
 *
 * @param setFormData - The wizard's form-data state setter
 * @returns Selection state plus the handlers Step 2 needs
 */
export function useExistingSupplierSelection(
  setFormData: Dispatch<SetStateAction<SupplierFormData>>,
): UseExistingSupplierSelectionReturn {
  const [existingSupplierId, setExistingSupplierId] = useState<number | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<ExistingSupplierMatch | null>(null);

  const selectExisting = useCallback((match: ExistingSupplierMatch) => {
    setExistingSupplierId(match.supplier_id);
    setSelectedMatch(match);
    setFormData((prev) => ({
      ...prev,
      company_name: match.company_name,
      address_line_1: match.address_line_1 || '',
      address_line_2: match.address_line_2 || '',
      address_city: match.city || '',
      address_region: match.state || '',
      address_postcode: match.postal_code || '',
      address_country: match.country || '',
      currency: match.currency || prev.currency,
    }));
  }, [setFormData]);

  const changeSupplierName = useCallback((name: string) => {
    if (existingSupplierId !== null) {
      // Abandoning a selection: reset only the fields pre-filled from the
      // existing supplier so its details don't linger under a different name.
      setExistingSupplierId(null);
      setSelectedMatch(null);
      setFormData((prev) => ({
        ...prev,
        company_name: name,
        address_line_1: '',
        address_line_2: '',
        address_city: '',
        address_region: '',
        address_postcode: '',
        address_country: '',
      }));
    } else {
      setFormData((prev) => ({ ...prev, company_name: name }));
    }
  }, [existingSupplierId, setFormData]);

  const clearExisting = useCallback(() => {
    setExistingSupplierId(null);
    setSelectedMatch(null);
    setFormData((prev) => ({
      ...prev,
      company_name: '',
      address_line_1: '',
      address_line_2: '',
      address_city: '',
      address_region: '',
      address_postcode: '',
      address_country: '',
    }));
  }, [setFormData]);

  const lockedField = useCallback((key: keyof ExistingSupplierMatch) =>
    existingSupplierId !== null && !!(selectedMatch && selectedMatch[key]),
  [existingSupplierId, selectedMatch]);

  return {
    existingSupplierId,
    selectedMatch,
    selectExisting,
    changeSupplierName,
    clearExisting,
    lockedField,
  };
}
